import { CourseRole, OrgRole, SectionRole, UserRole } from "./types/roles";
import { InteractionMode, type PagePermissionType } from "./types/websocket-auth";

export enum ComputedRole {
  ORG_ADMIN = "orgAdmin",
  INSTRUCTOR = "instructor",
  STUDENT = "student",
  SECTION_ASSISTANT = "sectionAssistant",
  COURSE_ADMIN = "courseAdmin",
  SUPER_ADMIN = "superAdmin",
  GLOBAL_ADMIN = "globalAdmin",
  RECORDING_AGENT = "recordingAgent",
  UNKNOWN = "unknown",
}

// Capabilities at the page level
export enum PageCapability {
  CAN_VIEW_PAGE = "canViewPage",
  CAN_VIEW_OTHER_USERS_DATA = "canViewOtherUsersData",
  CAN_MAKE_RESPONSE = "canMakeResponse",
  CAN_AUTHOR_PAGE = "canAuthorPage",
}

// Capabilities at the section level
export enum SectionCapability {
  CAN_ACCESS_SECTION = "canAccessSection",
  CAN_AUTHOR_SECTION = "canAuthorSection",
  CAN_EDIT_STUDENT_DRAFT = "canEditStudentDraft",
  CAN_CREATE_CHAT_MESSAGE = "canCreateChatMessage",
  CAN_DELETE_CHAT_MESSAGE = "canDeleteChatMessage",
  CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT = "canMentionAnyBreakoutGroupInChat",
  CAN_MENTION_ALL_IN_CHAT = "canMentionAllInChat",
  CAN_PARTICIPATE_IN_SECTION = "canParticipateInSection",
  CAN_PRESENT_SECTION = "canPresentSection",
  CAN_START_AND_STOP_RECORDING = "canStartAndStopRecording",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

// Capabilities at the org level
export enum OrgCapability {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_AUTHOR_ORG = "canAuthorOrg",
  CAN_TRANSFER_OUTCOME_TREES = "canTransferOutcomeTrees",
  CAN_IMPERSONATE_USER = "canImpersonateUser",
  CAN_OBSERVE_VIRTUAL_CLASS = "canObserveVirtualClass",
  CAN_SEE_ALL_DOCS = "canSeeAllDocs",
  CAN_SEE_ALL_ORGS = "canSeeAllOrgs",
  CAN_SEE_ALL_ORG_USERS = "canSeeAllOrgUsers",
  CAN_USE_BETA_FEATURES = "canUseBetaFeatures",
  CAN_USE_DEBUG_FEATURES = "canUseDebugFeatures",
}

// Capabilities at the course level
export enum CourseCapability {
  CAN_AUTHOR_COURSE = "canAuthorCourse",
  CAN_EDIT_ENROLLMENT = "canEditEnrollment",
}

export const Capability = {
  ...SectionCapability,
  ...OrgCapability,
  ...CourseCapability,
};

/**
 * Whether this User is an admin (`super_admin` / `global_admin`).
 *
 * Use for *who the target user is* — e.g. hide them from org-admin lists, or
 * block org-admins from editing their profile / OrgUser role.
 *
 * Do not use for *what the current user can do* in a section/course/org; that
 * is `getComputedRole` / capability hooks (scoped roles win over User.role).
 */
export const userHasGlobalAuthority = (user: { role: UserRole | null }): boolean =>
  user.role === UserRole.SUPER_ADMIN || user.role === UserRole.GLOBAL_ADMIN;

export const ROLES_MAP: Record<SectionRole | OrgRole | CourseRole | UserRole, ComputedRole> = {
  super_admin: ComputedRole.SUPER_ADMIN,
  global_admin: ComputedRole.GLOBAL_ADMIN,
  recording_agent: ComputedRole.RECORDING_AGENT,
  admin: ComputedRole.ORG_ADMIN,
  instructor: ComputedRole.INSTRUCTOR,
  section_assistant: ComputedRole.SECTION_ASSISTANT,
  course_admin: ComputedRole.COURSE_ADMIN,
  student: ComputedRole.STUDENT,
};

// Course admins get the same section-level capabilities as org admins, but only for
// sections whose course they're assigned to (via CourseUser or CourseGroupingUser).
// Keep this list mirrored with ORG_ADMIN entries below.
const SECTION_CAPABILITIES_MAP: Record<SectionCapability, ComputedRole[]> = {
  [Capability.CAN_ACCESS_SECTION]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
    ComputedRole.STUDENT,
    ComputedRole.RECORDING_AGENT,
  ],
  [Capability.CAN_AUTHOR_SECTION]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_EDIT_STUDENT_DRAFT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_CREATE_CHAT_MESSAGE]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
    ComputedRole.STUDENT,
  ],
  [Capability.CAN_DELETE_CHAT_MESSAGE]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_MENTION_ALL_IN_CHAT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_PARTICIPATE_IN_SECTION]: [ComputedRole.STUDENT],
  // For now, only instructors/section assistants can present
  [Capability.CAN_PRESENT_SECTION]: [ComputedRole.INSTRUCTOR, ComputedRole.SECTION_ASSISTANT],
  [Capability.CAN_START_AND_STOP_RECORDING]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.IS_VISIBLE_IN_CLASS]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.STUDENT,
  ],
};

const ORG_CAPABILITIES_MAP: Record<OrgCapability, ComputedRole[]> = {
  [Capability.CAN_ACCESS_MONITORING_DASHBOARD]: [ComputedRole.SUPER_ADMIN],
  [Capability.CAN_AUTHOR_ORG]: [
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
  ],
  [Capability.CAN_TRANSFER_OUTCOME_TREES]: [
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
  ],
  [Capability.CAN_IMPERSONATE_USER]: [ComputedRole.SUPER_ADMIN],
  [Capability.CAN_SEE_ALL_DOCS]: [ComputedRole.RECORDING_AGENT],
  [Capability.CAN_SEE_ALL_ORGS]: [ComputedRole.SUPER_ADMIN, ComputedRole.GLOBAL_ADMIN],
  [Capability.CAN_SEE_ALL_ORG_USERS]: [ComputedRole.SUPER_ADMIN, ComputedRole.GLOBAL_ADMIN],
  [Capability.CAN_OBSERVE_VIRTUAL_CLASS]: [
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
  ],
  [Capability.CAN_USE_BETA_FEATURES]: [ComputedRole.SUPER_ADMIN],
  [Capability.CAN_USE_DEBUG_FEATURES]: [ComputedRole.SUPER_ADMIN],
};

const COURSE_CAPABILITIES_MAP: Record<CourseCapability, ComputedRole[]> = {
  [Capability.CAN_AUTHOR_COURSE]: [
    ComputedRole.COURSE_ADMIN,
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
  ],
  [Capability.CAN_EDIT_ENROLLMENT]: [
    ComputedRole.COURSE_ADMIN,
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPER_ADMIN,
    ComputedRole.GLOBAL_ADMIN,
  ],
};

export type GetComputedRoleParams = {
  userRole: UserRole | null;
  sectionRole?: SectionRole | null;
  orgRole?: OrgRole | null;
  courseRole?: CourseRole | null;
  isStudentPreview?: boolean;
};

export const getComputedRole = ({
  userRole,
  sectionRole,
  orgRole,
  courseRole,
  isStudentPreview = false,
}: GetComputedRoleParams): ComputedRole => {
  if (isStudentPreview) {
    return ComputedRole.STUDENT;
  }

  // Scoped roles win; a null org role contributes no authority so resolution continues
  // to User.role (RFC Global User Roles).
  const role = sectionRole || courseRole || orgRole || userRole;
  if (role) {
    return ROLES_MAP[role];
  }

  return ComputedRole.UNKNOWN;
};

// Section capabilities
export const hasSectionCapability = (computedRole: ComputedRole, capability: SectionCapability) =>
  SECTION_CAPABILITIES_MAP[capability].includes(computedRole);

export const getSectionCapabilities = (computedRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(SectionCapability).map((capability) => [
      capability,
      hasSectionCapability(computedRole, capability),
    ]),
  ) as Record<SectionCapability, boolean>;
};

// Org capabilities
export const hasOrgCapability = (userOrgRole: ComputedRole, capability: OrgCapability) => {
  return ORG_CAPABILITIES_MAP[capability].includes(userOrgRole);
};

export const getOrgCapabilities = (userOrgRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(OrgCapability).map((capability) => [
      capability,
      hasOrgCapability(userOrgRole, capability),
    ]),
  ) as Record<OrgCapability, boolean>;
};

// Course capabilities
export const hasCourseCapability = (userCourseRole: ComputedRole, capability: CourseCapability) => {
  return COURSE_CAPABILITIES_MAP[capability].includes(userCourseRole);
};

export const getCourseCapabilities = (userCourseRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(CourseCapability).map((capability) => [
      capability,
      hasCourseCapability(userCourseRole, capability),
    ]),
  ) as Record<CourseCapability, boolean>;
};

// Page capabilities
/** Whether the user can view this page (released for participants, or author / see-all-docs). */
export const getCanViewPage = (
  page: Pick<PagePermissionType, "released_at"> | null,
  computedRole: ComputedRole,
) => {
  const canSeeAllDocs = hasOrgCapability(computedRole, Capability.CAN_SEE_ALL_DOCS);
  const { canAuthorSection, canParticipateInSection } = getSectionCapabilities(computedRole);
  const isPageReleased = Boolean(page?.released_at);
  return canSeeAllDocs || canAuthorSection || (canParticipateInSection && isPageReleased);
};

const getCanViewOtherUsersData = (page: PagePermissionType | null, computedRole: ComputedRole) => {
  const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
  const canViewPage = getCanViewPage(page, computedRole);
  const isPageVisibleToAll =
    canViewPage && Boolean(page?.last_rolled_up_at || page?.class_responses_released_at);
  return canAuthorSection || isPageVisibleToAll;
};

const getCanMakeResponse = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
  readyPageGroup: boolean,
) => {
  if (!page) {
    return false;
  }
  // Only students can make responses
  const canParticipateInSection = hasSectionCapability(
    computedRole,
    Capability.CAN_PARTICIPATE_IN_SECTION,
  );
  const canViewPage = getCanViewPage(page, computedRole);
  if (!canParticipateInSection || !canViewPage || page.locked_at) {
    return false;
  }
  const isGroupPage =
    page.interaction_mode &&
    [InteractionMode.ASSIGN_STUDENTS, InteractionMode.SELF_ASSIGN].includes(page.interaction_mode);

  // If it's a group page user has to be in a group and ready to make a response
  return !isGroupPage || readyPageGroup;
};

const getCanAuthorPage = (computedRole: ComputedRole) => {
  return hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
};

export const getPageCapabilities = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
  readyPageGroup: boolean,
): Record<PageCapability, boolean> => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, computedRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, computedRole),
    [PageCapability.CAN_MAKE_RESPONSE]: getCanMakeResponse(page, computedRole, readyPageGroup),
    [PageCapability.CAN_AUTHOR_PAGE]: getCanAuthorPage(computedRole),
  };
};

export const getPageCapabilitiesForServer = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
) => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, computedRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, computedRole),
  };
};
